import { BaseUser, User } from "@models";
import {
  ButtonColor,
  DocumentStatus,
  FormData,
  TemplateConfig,
  UserApprovalRoles,
} from "@types";
import { getDependantTemplates, getDocTypeStatus } from "@utils";
import {
  Document,
  DocumentVersion,
  DocumentVersionApprover,
  DocumentVersionApproverStatus,
} from "src/stores/models/document";
import { hasRequiredApproverCount } from "src/utils/documentApprovalPolicy";

export const getPreviousDocVersionWithRevision = (document: Document) => {
  const secondToLastVersion = document.versions[1];

  if (document.versions.length < 2 || secondToLastVersion?.revision === null)
    return;
  return secondToLastVersion;
};

export const getCurrentUserApprovalStatus = (
  approvers: DocumentVersionApprover[],
  userId: string
): DocumentVersionApproverStatus => {
  const userInSavedApprovers = approvers.find(
    (approver) => approver.user.id === userId
  );
  return (
    userInSavedApprovers?.approvalStatus ||
    DocumentVersionApproverStatus.AWAITING_SIGNATURE
  );
};

export const initialFormData: FormData = {
  selectedUsers: [],
  selectedAuthor: null,
  revisionSummary: "",
  documentMetadata: {
    title: "",
    regulatoryType: null,
    functionalGroupId: null,
  },
};

export const getDependentDocs = (
  recentlyUpdatedElementIds: string[],
  templatesConfig: TemplateConfig,
  documents: Document[],
  user: User,
  orgId: string,
  templateKey: string
): [string, string][] =>
  getDependantTemplates(recentlyUpdatedElementIds, templatesConfig)
    .filter(([template, _]) => {
      const status = getDocTypeStatus({
        documents,
        templateKey: template,
        user,
        orgId,
      });

      const isUnapprovedOrLive =
        status === DocumentStatus.UNAPPROVED || status === DocumentStatus.LIVE;
      const isNotSelf = template !== templateKey;

      return isUnapprovedOrLive && isNotSelf;
    })
    .map(([template, title]) => {
      const doc = documents.find((d) => d.templateKey === template);
      return [doc?.id ?? template, title] as [string, string];
    });

export const getUserById = (
  userId: string | null | undefined,
  orgMembers: BaseUser[]
) => {
  if (!userId || !orgMembers) return null;
  return orgMembers.find((user) => user.id === userId) || null;
};

export const getUserRole = (
  _approvers: DocumentVersionApprover[],
  currentUserId: string,
  _selectedDocVersion: DocumentVersion,
  formData: FormData
) => {
  const authorId = formData.selectedAuthor?.id;

  const isNotInvolved =
    formData.selectedUsers.find((a) => a?.id === currentUserId) === undefined;

  switch (true) {
    case isNotInvolved:
      return UserApprovalRoles.NOT_INVOLVED;
    case authorId === currentUserId:
      return UserApprovalRoles.AUTHOR;
    default:
      return UserApprovalRoles.APPROVER;
  }
};

const hasFormBeenFormUpdated = (
  formData: FormData,
  selectedDocVersion: DocumentVersion,
  document: Document,
  approvers: DocumentVersionApprover[]
) => {
  const hasDocumentMetadataChanged =
    JSON.stringify(formData.documentMetadata) !==
    JSON.stringify({
      title: document.title,
      regulatoryType: document.regulatoryType,
      functionalGroupId: document.functionalGroupId,
    });

  const hasRevisionSummaryChanged =
    formData.revisionSummary !== selectedDocVersion.revisionSummary;

  const hasAuthorChanged =
    formData.selectedAuthor?.id !== selectedDocVersion.createdBy;

  const haveApproversChanged =
    approvers.some(
      (savedApprover) =>
        savedApprover.user.id !==
        formData.selectedUsers.find((u) => u?.id === savedApprover.user.id)?.id
    ) ||
    formData.selectedUsers.some(
      (u) =>
        u?.id !==
        approvers.find((a) => a.user.id === u?.id)?.user.id
    );

  return (
    hasAuthorChanged ||
    hasRevisionSummaryChanged ||
    hasDocumentMetadataChanged ||
    haveApproversChanged
  );
};

const isFormDataInvalid = (
  formData: FormData,
  showMetadataValidation: boolean,
  minimumApprovers: number
) =>
  formData.revisionSummary === "" ||
  formData.selectedUsers.length < minimumApprovers ||
  formData.selectedAuthor === null ||
  (showMetadataValidation &&
    (formData.documentMetadata.title === null ||
      formData.documentMetadata.regulatoryType === null ||
      formData.documentMetadata.functionalGroupId === null));

export const hasFormBeenInitialised = (
  selectedDocVersion: DocumentVersion,
  document: Document,
  approverCount = selectedDocVersion.documentVersionApprover.length
) =>
  approverCount > 0 &&
  selectedDocVersion.revisionSummary !== "" &&
  document.title !== "" &&
  document.regulatoryType !== null &&
  document.functionalGroupId !== null;

export const shouldStartInEditMode = (
  selectedDocVersion: DocumentVersion,
  document: Document,
  approverCount: number
) =>
  !hasRequiredApproverCount(approverCount) ||
  !hasFormBeenInitialised(selectedDocVersion, document, approverCount);

export const getButtonStateForRole = (
  role: UserApprovalRoles,
  formData: FormData,
  selectedDocVersion: DocumentVersion,
  approvers: DocumentVersionApprover[],
  document: Document,
  currentUserId: string,
  isEditMode: boolean,
  showMetadataValidation: boolean,
  minimumApprovers: number
): {
  buttonText: string;
  buttonColor: ButtonColor;
  buttonDisabled: boolean;
  buttonHidden?: boolean;
} => {
  const hasFormDataChanged = hasFormBeenFormUpdated(
    formData,
    selectedDocVersion,
    document,
    approvers
  );

  const isDataInvalid = isFormDataInvalid(
    formData,
    showMetadataValidation,
    minimumApprovers
  );

  switch (role) {
    case UserApprovalRoles.AUTHOR:
    case UserApprovalRoles.APPROVER:
      const currentUserApprovalStatus =
        approvers.find((approver) => approver.user.id === currentUserId)
          ?.approvalStatus;
      const isApproved =
        currentUserApprovalStatus === DocumentVersionApproverStatus.APPROVED;
      if (isEditMode === false) {
        return {
          buttonText: isApproved ? "Unapprove" : "Review And Approve",
          buttonColor: isApproved ? "error" : "success",
          buttonDisabled: !currentUserApprovalStatus,
        };
      }

      return {
        buttonText: "Submit for Approval",
        buttonColor: "primary",
        buttonDisabled: isDataInvalid || !hasFormDataChanged,
      };

    default:
      if (!isEditMode) {
        return {
          buttonText: "Submit for Approval",
          buttonColor: "primary",
          buttonDisabled: true,
          buttonHidden: true,
        };
      }

      return {
        buttonText: "Submit for Approval",
        buttonColor: "primary",
        buttonDisabled: isDataInvalid || !hasFormDataChanged,
      };
  }
};

export const includeAuthorInApprovers = (
  users: Array<BaseUser | null>,
  selectedAuthor: BaseUser | null
): Array<BaseUser | null> => [
  selectedAuthor,
  ...users.filter((user) => user?.id !== selectedAuthor?.id),
];
